import { useEffect } from "react";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { getClinicianSocket } from "@/lib/clinicianSocket";
import { onResync } from "@/lib/socketRecovery";
import { STALE, unwrap } from "@/lib/query/queryClient";
import {
  claimReviewItemApi,
  getClinicianReviewItemApi,
  listMyReviewItemsApi,
  listReviewQueueApi,
  previewQueueItemApi,
  releaseReviewItemApi,
  type MyReviewItem,
  type QueueItem,
  type QueueItemPreview,
} from "./reviewQueueApi";

/** IDENTITY_MISMATCH items are patient-only: the backend never lists them for a
 *  clinician, and the clinician side never shows one even if it did. */
const withoutIdentityChecks = <T extends { triggerType: string }>(items: T[]) =>
  items.filter((i) => i.triggerType !== "IDENTITY_MISMATCH");

/** Query keys for the clinician portal. */
export const clinicianKeys = {
  all: ["clinician"] as const,
  queue: ["clinician", "queue"] as const,
  mine: ["clinician", "mine"] as const,
  /** Prefix for every held item. */
  items: ["clinician", "item"] as const,
  item: (id: string) => ["clinician", "item", id] as const,
  /** Prefix for every shared-queue preview. */
  previews: ["clinician", "preview"] as const,
  preview: (id: string) => ["clinician", "preview", id] as const,
};

/** The shared queue: unclaimed items, oldest first. */
export const queueQuery = () =>
  queryOptions({
    queryKey: clinicianKeys.queue,
    queryFn: async () => withoutIdentityChecks((await unwrap(listReviewQueueApi())).items),
    staleTime: STALE.live,
    meta: { errorToast: "Unable to load the shared queue." },
  });

/** The clinician's own items (claimed or assigned). */
export const myItemsQuery = () =>
  queryOptions({
    queryKey: clinicianKeys.mine,
    queryFn: async () => withoutIdentityChecks((await unwrap(listMyReviewItemsApi())).items),
    staleTime: STALE.live,
    meta: { errorToast: "Unable to load your items." },
  });

export const heldItemQuery = (id: string) =>
  queryOptions({
    queryKey: clinicianKeys.item(id),
    queryFn: () => unwrap(getClinicianReviewItemApi(id)),
    staleTime: STALE.live,
  });

export const previewQuery = (id: string) =>
  queryOptions({
    queryKey: clinicianKeys.preview(id),
    queryFn: () => unwrap(previewQueueItemApi(id)),
    staleTime: STALE.live,
  });

/**
 * Claim from the shared queue. The row moves from the queue to My items
 * instantly (both put back if the claim is refused); on success the held item
 * is seeded into the cache so it opens at once, and both lists refetch.
 */
export function useClaimItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(claimReviewItemApi(id)),
    onMutate: async (id) => {
      await Promise.all([
        client.cancelQueries({ queryKey: clinicianKeys.queue }),
        client.cancelQueries({ queryKey: clinicianKeys.mine }),
      ]);
      const previous = client.getQueryData<QueueItem[]>(clinicianKeys.queue);
      const previousMine = client.getQueryData<MyReviewItem[]>(clinicianKeys.mine);
      const row = previous?.find((i) => i.id === id);
      if (previous) client.setQueryData(clinicianKeys.queue, previous.filter((i) => i.id !== id));
      if (row && previousMine && !previousMine.some((i) => i.id === id)) {
        const preview = client.getQueryData<QueueItemPreview>(clinicianKeys.preview(id));
        client.setQueryData<MyReviewItem[]>(clinicianKeys.mine, [...previousMine, {
          id, triggerType: row.triggerType, medicineName: row.medicineName,
          itemStatus: "IN_CLINICIAN_REVIEW", attachedVia: "CLAIMED", attachedAt: new Date().toISOString(),
          patientAge: preview?.patientAge, awaitingPatientReply: preview?.awaitingPatientReply,
        }]);
      }
      return { previous, previousMine };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) client.setQueryData(clinicianKeys.queue, context.previous);
      if (context?.previousMine) client.setQueryData(clinicianKeys.mine, context.previousMine);
    },
    onSuccess: (item, id) => {
      if (item) {
        client.setQueryData(clinicianKeys.item(id), item);
        // The claim's answer is the real thing: My items shows it until the refetch lands.
        client.setQueryData<MyReviewItem[]>(clinicianKeys.mine, (mine) => mine?.map((i) => i.id !== id ? i : {
          ...i, itemStatus: item.itemStatus, attachedVia: item.attachedVia, attachedAt: item.attachedAt,
          patientAge: item.patientAge, awaitingPatientReply: item.awaitingPatientReply,
        }));
      }
      client.removeQueries({ queryKey: clinicianKeys.preview(id) });
    },
    onSettled: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: clinicianKeys.queue }),
        client.invalidateQueries({ queryKey: clinicianKeys.mine }),
      ]),
  });
}

/** Drops an item that has left this clinician (released, decided, closed) from My items and the cache. */
export function forgetHeldItem(client: ReturnType<typeof useQueryClient>, id: string) {
  client.setQueryData<{ id: string }[]>(clinicianKeys.mine, (mine) => mine?.filter((i) => i.id !== id));
  client.removeQueries({ queryKey: clinicianKeys.item(id) });
  void client.invalidateQueries({ queryKey: clinicianKeys.mine });
}

/** Release back to the shared queue. */
export function useReleaseItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(releaseReviewItemApi(id)),
    onSuccess: (_data, id) => {
      forgetHeldItem(client, id);
      void client.invalidateQueries({ queryKey: clinicianKeys.queue });
    },
  });
}

/**
 * Clinician realtime → cache: the shared queue changed (item added/removed,
 * room "clinicians"); my items changed (a notification, or reassigned /
 * returned without one). Everything on screen refetches in place; after a
 * reconnect / server resync, everything is revalidated.
 */
export function useClinicianLiveQueries(): void {
  const client = useQueryClient();
  useEffect(() => {
    const socket = getClinicianSocket();
    const onQueue = () => {
      void client.invalidateQueries({ queryKey: clinicianKeys.queue });
      void client.invalidateQueries({ queryKey: clinicianKeys.previews });
    };
    const onMine = () => {
      void client.invalidateQueries({ queryKey: clinicianKeys.mine });
      void client.invalidateQueries({ queryKey: clinicianKeys.items });
    };
    socket.on("review_queue:item_added", onQueue);
    socket.on("review_queue:item_removed", onQueue);
    socket.on("notification:new", onMine);
    socket.on("review_items:changed", onMine);
    const offResync = onResync(socket, () => void client.invalidateQueries({ queryKey: clinicianKeys.all }));
    return () => {
      socket.off("review_queue:item_added", onQueue);
      socket.off("review_queue:item_removed", onQueue);
      socket.off("notification:new", onMine);
      socket.off("review_items:changed", onMine);
      offResync();
    };
  }, [client]);
}
